import { ChangeDetectionStrategy, Component, input, output, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import type { ProductVariantDto, ProductVariantGroupDto } from '@api/models';
import { buildColorSwatchBackground } from '@/helpers/build-color-swatch-background.function';
import { RouterLinkWithLang } from '@/directives/router-link-with-lang/router-link-with-lang.directive';

/** Colour rows longer than this start folded, with a "Всі кольори (N)" toggle */
const COLLAPSED_COLORS_LIMIT = 24;

/**
 * The axis selectors of a variant group: one row per axis, every value of it. A value is a plain
 * link to the sibling that has it - a regular navigation, so back returns to the variant before. A
 * value no sibling has together with the current choice on the other axes still shows, dashed, and
 * leads to the nearest sibling; its tooltip says what else changes.
 *
 * On a list card the rows are the same axes, a few values each - a colour row unlabelled - and the
 * card listens to `variantHover` to show the hovered variant. `moreSlug` adds the "+N" link for the
 * values left off the card.
 */
@Component({
  selector: 'product-variants',
  templateUrl: './product-variants.component.html',
  styleUrls: ['./product-variants.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, RouterLinkWithLang, TranslatePipe],
})
export class ProductVariantsComponent {

  readonly variantGroups = input<ProductVariantGroupDto[]>([]);
  readonly isBigSize = input<boolean>(false);
  /** The page "+N" leads to - the variant the card shows; no "+N" without it */
  readonly moreSlug = input<string | null>(null);

  readonly variantHover = output<ProductVariantDto>();

  /** Rows the shopper unfolded, by attribute id */
  protected readonly expandedGroupIds = signal<ReadonlySet<string>>(new Set());

  protected readonly collapsedColorsLimit = COLLAPSED_COLORS_LIMIT;

  protected isFoldable(group: ProductVariantGroupDto): boolean {
    return this.isBigSize() && group.hasColor && group.variants.length > COLLAPSED_COLORS_LIMIT;
  }

  /** A folded row still shows the selected value, even when it sits past the limit */
  protected visibleVariants(group: ProductVariantGroupDto): ProductVariantDto[] {
    if (!this.isFoldable(group) || this.expandedGroupIds().has(group.attributeId)) {
      return group.variants;
    }

    const visible = group.variants.slice(0, COLLAPSED_COLORS_LIMIT);
    const selected = group.variants.find(variant => variant.isSelected);
    if (selected && !visible.includes(selected)) {
      visible[visible.length - 1] = selected;
    }
    return visible;
  }

  protected toggleGroup(group: ProductVariantGroupDto): void {
    const expanded = new Set(this.expandedGroupIds());
    if (expanded.has(group.attributeId)) {
      expanded.delete(group.attributeId);
    } else {
      expanded.add(group.attributeId);
    }
    this.expandedGroupIds.set(expanded);
  }

  protected swatchBackground(variant: ProductVariantDto): string | null {
    return buildColorSwatchBackground(variant.color);
  }
}
